<template>
  <div class="p-6 bg-white rounded-lg shadow-md">
    <h2 class="text-2xl font-bold mb-4">Supabase Integration Test</h2>
    
    <div v-if="loading" class="mb-4 p-3 bg-blue-100 text-blue-700 rounded-md">
      Loading...
    </div>
    
    <div v-if="error" class="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
      Error: {{ error.message }}
    </div>
    
    <div v-if="isAuthenticated" class="mb-6">
      <div class="p-4 bg-green-100 text-green-700 rounded-md mb-4">
        <p class="font-bold">✅ Authenticated</p>
        <p>User ID: {{ user?.id }}</p>
        <p>Email: {{ user?.email }}</p>
        <p>Name: {{ profile?.name || 'Not set' }}</p>
        <p>Username: {{ profile?.username || 'Not set' }}</p>
        <p>Business Role: <span class="font-medium">{{ profile?.business_role ? formatBusinessRole(profile.business_role as BusinessRole) : 'Not set' }}</span></p>
        <p>System Role: <span class="font-medium">{{ profile?.system_role ? SystemRole[profile.system_role] : 'Not set' }}</span></p>
      </div>
      
      <button 
        @click="handleLogout" 
        class="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
        :disabled="loading"
      >
        Logout
      </button>
    </div>
    
    <div v-else class="mb-6">
      <div class="p-4 bg-yellow-100 text-yellow-700 rounded-md mb-4">
        <p class="font-bold">⚠️ Not Authenticated</p>
        <p>Please login or register to test Supabase authentication.</p>
      </div>
      
      <div class="flex space-x-4">
        <button 
          @click="showLoginForm = true; showRegisterForm = false" 
          class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
        >
          Show Login Form
        </button>
        
        <button 
          @click="showRegisterForm = true; showLoginForm = false" 
          class="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
        >
          Show Register Form
        </button>
      </div>
    </div>
    
    <!-- Login Form -->
    <div v-if="!isAuthenticated && showLoginForm" class="mt-6 p-4 border border-gray-200 rounded-md">
      <h3 class="text-xl font-bold mb-4">Login</h3>
      
      <form @submit.prevent="handleLogin" class="space-y-4">
        <div>
          <label for="login-email" class="block text-sm font-medium mb-1">Email</label>
          <input 
            type="email" 
            id="login-email" 
            v-model="loginForm.email" 
            class="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>
        
        <div>
          <label for="login-password" class="block text-sm font-medium mb-1">Password</label>
          <input 
            type="password" 
            id="login-password" 
            v-model="loginForm.password" 
            class="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>
        
        <button 
          type="submit" 
          class="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          :disabled="loading"
        >
          Login
        </button>
      </form>
    </div>
    
    <!-- Register Form -->
    <div v-if="!isAuthenticated && showRegisterForm" class="mt-6 p-4 border border-gray-200 rounded-md">
      <h3 class="text-xl font-bold mb-4">Register</h3>
      
      <form @submit.prevent="handleRegister" class="space-y-4">
        <div>
          <label for="register-fullname" class="block text-sm font-medium mb-1">Full Name</label>
          <input 
            type="text" 
            id="register-fullname" 
            v-model="registerForm.fullname" 
            class="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>
        
        <div>
          <label for="register-email" class="block text-sm font-medium mb-1">Email</label>
          <input 
            type="email" 
            id="register-email" 
            v-model="registerForm.email" 
            class="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>
        
        <div>
          <label for="register-username" class="block text-sm font-medium mb-1">Username</label>
          <input 
            type="text" 
            id="register-username" 
            v-model="registerForm.username" 
            class="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>
        
        <div>
          <label for="register-password" class="block text-sm font-medium mb-1">Password</label>
          <input 
            type="password" 
            id="register-password" 
            v-model="registerForm.password" 
            class="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>
        
        <div>
          <label for="register-confirm-password" class="block text-sm font-medium mb-1">Confirm Password</label>
          <input 
            type="password" 
            id="register-confirm-password" 
            v-model="registerForm.confirmPassword" 
            class="w-full px-3 py-2 border border-gray-300 rounded-md"
            required
          />
        </div>
        
        <div v-if="passwordMismatch" class="p-3 bg-red-100 text-red-700 rounded-md">
          Passwords do not match
        </div>
        
        <button 
          type="submit" 
          class="w-full px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
          :disabled="loading || passwordMismatch"
        >
          Register
        </button>
      </form>
    </div>
    
    <div class="mt-6">
      <h3 class="text-xl font-bold mb-2">Database Test</h3>
      <div class="flex flex-wrap gap-2 mb-4">
        <button
          @click="testDatabaseConnection"
          class="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors"
          :disabled="dbLoading"
        >
          <span v-if="dbLoading">Testing...</span>
          <span v-else>Test Database Connection</span>
        </button>
        
        <button
          @click="checkSupabaseConfig"
          class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          :disabled="configCheckLoading"
        >
          <span v-if="configCheckLoading">Checking...</span>
          <span v-else>Check Supabase Config</span>
        </button>
      </div>
      
      <div v-if="isAuthenticated" class="mb-4">
        <button
          @click="testBusinessRoleUpdate"
          class="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors"
          :disabled="businessRoleUpdateLoading"
        >
          <span v-if="businessRoleUpdateLoading">Updating...</span>
          <span v-else>Test Business Role Update</span>
        </button>
      </div>
      
      <div v-if="dbTestResult" class="mt-4">
        <div v-if="dbTestResult.success" class="p-4 bg-green-100 text-green-700 rounded-md">
          <h4 class="font-bold">Database Connection Successful!</h4>
          <pre class="whitespace-pre-wrap">{{ JSON.stringify(dbTestResult.data, null, 2) }}</pre>
        </div>
        <div v-else class="p-4 bg-red-100 text-red-700 rounded-md">
          <h4 class="font-bold">Database Connection Error</h4>
          <p>{{ dbTestResult.message }}</p>
          
          <div v-if="dbTestResult.error && dbTestResult.error.code === '42P01'" class="mt-4 border-t border-red-200 pt-4">
            <h5 class="font-bold">Missing Table Error</h5>
            <p class="mb-2">The required database tables have not been created yet. Please create the following tables in your Supabase instance:</p>
            <ul class="list-disc pl-5 mb-4">
              <li>{{ DatabaseTable.PROFILES }}</li>
              <li>{{ DatabaseTable.TEMPLATES }}</li>
              <li>{{ DatabaseTable.FORECASTING_PROCESS }}</li>
            </ul>
            <p>You can find the SQL scripts to create these tables in the <code>SUPABASE_INTEGRATION.md</code> file.</p>
          </div>
          
          <pre v-if="dbTestResult.error" class="mt-4 whitespace-pre-wrap bg-red-50 p-2 rounded">{{ JSON.stringify(dbTestResult.error, null, 2) }}</pre>
        </div>
      </div>
      
      <div v-if="configCheckResult" class="mt-4">
        <div v-if="configCheckResult.success" class="p-4 bg-green-100 text-green-700 rounded-md">
          <h4 class="font-bold">Supabase Configuration Check Successful!</h4>
          <pre class="whitespace-pre-wrap">{{ JSON.stringify(configCheckResult.data, null, 2) }}</pre>
        </div>
        <div v-else class="p-4 bg-red-100 text-red-700 rounded-md">
          <h4 class="font-bold">Supabase Configuration Check Error</h4>
          <p>{{ configCheckResult.message }}</p>
          <pre v-if="configCheckResult.error" class="mt-4 whitespace-pre-wrap bg-red-50 p-2 rounded">{{ JSON.stringify(configCheckResult.error, null, 2) }}</pre>
        </div>
      </div>
      
      <div v-if="businessRoleUpdateResult" class="mt-4">
        <div v-if="businessRoleUpdateResult.success" class="p-4 bg-green-100 text-green-700 rounded-md">
          <h4 class="font-bold">Business Role Update Successful!</h4>
          <p>Previous role: {{ formatBusinessRole(businessRoleUpdateResult.previousRole as BusinessRole) }}</p>
          <p>New role: {{ formatBusinessRole(businessRoleUpdateResult.newRole as BusinessRole) }}</p>
          <pre class="whitespace-pre-wrap mt-2">{{ JSON.stringify(businessRoleUpdateResult.data, null, 2) }}</pre>
        </div>
        <div v-else class="p-4 bg-red-100 text-red-700 rounded-md">
          <h4 class="font-bold">Business Role Update Error</h4>
          <p>{{ businessRoleUpdateResult.message }}</p>
          <pre v-if="businessRoleUpdateResult.error" class="mt-4 whitespace-pre-wrap bg-red-50 p-2 rounded">{{ JSON.stringify(businessRoleUpdateResult.error, null, 2) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { supabase } from '@/services/supabase';
import type { LoginCredentials, RegistrationData } from '@/types/user';
import { DatabaseTestResult, DatabaseTable } from '@/types/database';
import { logger, LogCategory } from '@/services/logger';
import { handleError, ErrorType, withErrorHandling } from '@/utils/errorHandling';
import { BusinessRole, formatBusinessRole } from '@/types/businessRole';
import { SystemRole } from '@/types/systemRole';

export default defineComponent({
  name: 'SupabaseTest',
  setup() {
    const {
      user,
      profile,
      loading,
      error,
      isAuthenticated,
      login,
      register,
      logout,
      init,
      updateProfile
    } = useAuth();
    
    const showLoginForm = ref(false);
    const showRegisterForm = ref(false);
    const dbTestResult = ref<DatabaseTestResult | null>(null);
    const dbLoading = ref(false);
    const businessRoleUpdateLoading = ref(false);
    const configCheckLoading = ref(false);
    const businessRoleUpdateResult = ref<{
      success: boolean;
      message: string;
      data?: any;
      error?: any;
      previousRole?: string;
      newRole?: string;
    } | null>(null);
    const configCheckResult = ref<{
      success: boolean;
      message: string;
      data?: any;
      error?: any;
    } | null>(null);
    
    const loginForm = ref<LoginCredentials>({
      email: '',
      password: ''
    });
    
    const registerForm = ref<RegistrationData>({
      fullname: '',
      email: '',
      username: '',
      password: '',
      confirmPassword: ''
    });
    
    const passwordMismatch = computed(() => {
      return registerForm.value.password !== registerForm.value.confirmPassword && 
             registerForm.value.confirmPassword.length > 0;
    });
    
    onMounted(() => {
      init();
    });
    
    async function handleLogin() {
      await withErrorHandling(async () => {
        logger.info('Attempting login', LogCategory.AUTH, { email: loginForm.value.email });
        
        await login(loginForm.value);
        
        logger.info('Login successful', LogCategory.AUTH, { email: loginForm.value.email });
        
        showLoginForm.value = false;
        loginForm.value = { email: '', password: '' };
        
        return true;
      }, error, ErrorType.AUTHENTICATION, 'Login failed');
    }
    
    async function handleRegister() {
      if (passwordMismatch.value) {
        return;
      }
      
      await withErrorHandling(async () => {
        logger.info('Attempting registration', LogCategory.AUTH, {
          email: registerForm.value.email,
          username: registerForm.value.username
        });
        
        await register(registerForm.value);
        
        logger.info('Registration successful', LogCategory.AUTH, {
          email: registerForm.value.email,
          username: registerForm.value.username
        });
        
        showRegisterForm.value = false;
        registerForm.value = {
          fullname: '',
          email: '',
          username: '',
          password: '',
          confirmPassword: ''
        };
        
        return true;
      }, error, ErrorType.AUTHENTICATION, 'Registration failed');
    }
    
    async function handleLogout() {
      await withErrorHandling(async () => {
        logger.info('Attempting logout', LogCategory.AUTH);
        
        await logout();
        
        logger.info('Logout successful', LogCategory.AUTH);
        
        return true;
      }, error, ErrorType.AUTHENTICATION, 'Logout failed');
    }
    
    async function testDatabaseConnection() {
      dbLoading.value = true;
      dbTestResult.value = null;
      
      try {
        await withErrorHandling(async () => {
          logger.info('Testing database connection', LogCategory.DATABASE, { table: DatabaseTable.PROFILES });
          
          // Test a simple query to check if the database connection works
          const { data, error: dbError } = await supabase
            .from(DatabaseTable.PROFILES)
            .select('count')
            .limit(1);
          
          if (dbError) {
            throw handleError(dbError, error, ErrorType.DATABASE,
              'Failed to connect to database', { table: DatabaseTable.PROFILES });
          }
          
          dbTestResult.value = {
            success: true,
            message: 'Database connection successful',
            data
          };
          
          logger.info('Database connection test successful', LogCategory.DATABASE, {
            table: DatabaseTable.PROFILES,
            result: data
          });
          
          return data;
        }, error, ErrorType.DATABASE, 'Database connection test failed');
      } catch (err) {
        const errorObj = err as any;
        dbTestResult.value = {
          success: false,
          message: errorObj.message || 'Unknown error',
          error: {
            code: errorObj.code,
            message: errorObj.message,
            details: errorObj.details
          }
        };
      } finally {
        dbLoading.value = false;
      }
    }
    
    async function checkSupabaseConfig() {
      configCheckLoading.value = true;
      configCheckResult.value = null;
      
      try {
        logger.info('Checking Supabase configuration', LogCategory.GENERAL);
        
        // Check environment variables
        const supabaseUrl = process.env.VUE_APP_SUPABASE_URL;
        const supabaseAnonKey = process.env.VUE_APP_SUPABASE_ANON_KEY;
        const supabaseServiceKey = process.env.VUE_APP_SUPABASE_SERVICE_KEY;
        
        logger.debug('Supabase URL: ' + (supabaseUrl ? 'Available' : 'Missing'), LogCategory.GENERAL);
        logger.debug('Supabase Anon Key: ' + (supabaseAnonKey ? 'Available' : 'Missing'), LogCategory.GENERAL);
        logger.debug('Supabase Service Key: ' + (supabaseServiceKey ? 'Available' : 'Missing'), LogCategory.GENERAL);
        
        // Check if we can get the current session
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        logger.debug('Session check result: ' + (sessionError ? 'Error' : 'Success'), LogCategory.GENERAL);
        logger.debug('Session available: ' + (sessionData.session ? 'Yes' : 'No'), LogCategory.GENERAL);
        
        
        if (sessionError) {
          throw sessionError;
        }

        // Check if we can access the profiles table
        const { error: profileError } = await supabase
          .from('profiles')
          .select('count')
          .limit(1);

        logger.debug('Profiles table check result: ' + (profileError ? 'Error' : 'Success'), LogCategory.GENERAL);
        
        if (profileError) {
          throw profileError;
        }
        
        configCheckResult.value = {
          success: true,
          message: 'Supabase configuration check successful',
          data: {
            environment: {
              supabaseUrl: supabaseUrl ? 'Available' : 'Missing',
              supabaseAnonKey: supabaseAnonKey ? 'Available' : 'Missing',
              supabaseServiceKey: supabaseServiceKey ? 'Available' : 'Missing'
            },
            session: {
              available: sessionData.session ? true : false,
              userId: sessionData.session?.user?.id || null
            },
            database: {
              profilesTableAccessible: true
            }
          }
        };
        
        logger.info('Supabase configuration check successful', LogCategory.GENERAL);
      } catch (err) {
        const errorObj = err as any;
        configCheckResult.value = {
          success: false,
          message: errorObj.message || 'Unknown error',
          error: {
            code: errorObj.code,
            message: errorObj.message,
            details: errorObj.details
          }
        };
        
        logger.error('Supabase configuration check failed', LogCategory.GENERAL, { error: err });
      } finally {
        configCheckLoading.value = false;
      }
    }
    
    async function testBusinessRoleUpdate() {
      logger.info('Testing business role update', LogCategory.DATABASE);
      logger.debug('User state: ' + (user.value ? 'User is set' : 'User is NOT set'), LogCategory.DATABASE);
      logger.debug('Profile state: ' + (profile.value ? 'Profile is set' : 'Profile is NOT set'), LogCategory.DATABASE);
      
      if (!user.value || !profile.value) {
        logger.error('User or profile is not set', LogCategory.DATABASE);
        businessRoleUpdateResult.value = {
          success: false,
          message: 'User not authenticated',
        };
        return;
      }
      
      logger.debug('User ID: ' + user.value.id, LogCategory.DATABASE);
      logger.debug('User email: ' + user.value.email, LogCategory.DATABASE);
      logger.debug('Current profile available', LogCategory.DATABASE, { profile: profile.value });
      
      businessRoleUpdateLoading.value = true;
      businessRoleUpdateResult.value = null;
      
      try {
        // Get current business role
        const currentRole = profile.value.business_role as BusinessRole;
        logger.debug('Current business role: ' + currentRole, LogCategory.DATABASE);
        
        // Determine new role (cycle through available roles)
        const roles = Object.values(BusinessRole);
        logger.debug('Available roles', LogCategory.DATABASE, { roles });
        const currentIndex = roles.indexOf(currentRole as BusinessRole);
        logger.debug('Current index in roles array: ' + currentIndex, LogCategory.DATABASE);
        const nextIndex = (currentIndex + 1) % roles.length;
        logger.debug('Next index in roles array: ' + nextIndex, LogCategory.DATABASE);
        const newRole = roles[nextIndex];
        logger.debug('New role to set: ' + newRole, LogCategory.DATABASE);
        
        logger.info('Testing business role update', LogCategory.DATABASE, {
          userId: user.value.id,
          currentRole,
          newRole
        });
        
        logger.debug('About to call updateProfile with business_role: ' + newRole, LogCategory.DATABASE);
        // Use the updateProfile function from the same useAuth instance
        // that was created in the setup function
        const updatedProfile = await updateProfile({
          business_role: newRole
        });
        
        logger.debug('updateProfile result received', LogCategory.DATABASE, { updatedProfile });
        
        if (!updatedProfile) {
          throw new Error('Failed to update business role');
        }
        
        businessRoleUpdateResult.value = {
          success: true,
          message: 'Business role updated successfully',
          data: updatedProfile,
          previousRole: currentRole,
          newRole: newRole
        };
        
        logger.info('Business role update successful', LogCategory.DATABASE, {
          userId: user.value.id,
          previousRole: currentRole,
          newRole: newRole
        });
      } catch (err) {
        const errorObj = err as any;
        businessRoleUpdateResult.value = {
          success: false,
          message: errorObj.message || 'Unknown error',
          error: {
            code: errorObj.code,
            message: errorObj.message,
            details: errorObj.details
          }
        };
        
        logger.error('Business role update failed', LogCategory.DATABASE, {
          error: err
        });
      } finally {
        businessRoleUpdateLoading.value = false;
      }
    }
    
    return {
      user,
      profile,
      loading,
      error,
      isAuthenticated,
      showLoginForm,
      showRegisterForm,
      loginForm,
      registerForm,
      passwordMismatch,
      dbTestResult,
      dbLoading,
      businessRoleUpdateLoading,
      businessRoleUpdateResult,
      configCheckLoading,
      configCheckResult,
      handleLogin,
      handleRegister,
      handleLogout,
      testDatabaseConnection,
      checkSupabaseConfig,
      testBusinessRoleUpdate,
      // Make enums available in template
      DatabaseTable,
      BusinessRole,
      SystemRole,
      formatBusinessRole
    };
  }
});
</script>
